// src/pages/OrdersPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Package,
  Clock,
  Truck,
  CheckCircle,
  XCircle,
  ChevronRight,
  Calendar,
  MapPin,
  CreditCard,
  Search,
  Eye,
} from 'lucide-react';
import { collection, query, where, orderBy, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import toast from 'react-hot-toast';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import { useSearch } from '../context/SearchContext';
import { openCartSidebar } from '../components/CartSidebar';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { getAllCampaigns } from '../services/discountService';
import { getAllProducts } from '../services/productService';

export default function OrdersPage() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [orderSearchQuery, setOrderSearchQuery] = useState(''); // ← DEĞİŞTİ: Sipariş araması için ayrı state
  const [filterStatus, setFilterStatus] = useState('all');

  const { cartCount } = useCart();
  const { wishlistCount } = useWishlist();
  const { searchQuery, setSearchQuery } = useSearch(); // ← Header araması için

  const userId = localStorage.getItem('userId');
  const userName = localStorage.getItem('userName') || 'Kullanıcı';

  useEffect(() => {
    if (!userId) {
      toast.error('Lütfen önce giriş yapın');
      navigate('/account');
      return;
    }

    loadOrders();
  }, [userId, navigate]);

  const loadOrders = async () => {
    try {
      setLoading(true);

      const [campaignsResult, productsResult] = await Promise.all([
        getAllCampaigns(),
        getAllProducts()
      ]);

      const ordersRef = collection(db, 'Orders');
      const q = query(
        ordersRef,
        where('customerId', '==', userId),
        orderBy('createdAt', 'desc')
      );

      const snapshot = await getDocs(q);
      const ordersData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || new Date(doc.data().createdAt)
      }));

      setOrders(ordersData);

      if (campaignsResult.success) {
        setCampaigns(campaignsResult.campaigns);
      }

      if (productsResult.success) {
        const activeProducts = productsResult.products.filter(product =>
          product.isActive !== false
        );
        setProducts(activeProducts);

        // Mevcut kategorileri tespit et
        const categoriesSet = new Set();
        activeProducts.forEach(product => {
          if (product.category) {
            const mainCat = product.category.includes(' > ')
              ? product.category.split(' > ')[0]
              : product.category;
            categoriesSet.add(mainCat);
          }
        });
        setAvailableCategories(Array.from(categoriesSet));
      }
    } catch (error) {
      console.error('Siparişler yüklenirken hata:', error);
      toast.error('Siparişler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  // ← Header arama fonksiyonu
  const handleHeaderSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  const getStatusInfo = (status) => {
    const statusMap = {
      pending: {
        label: 'Beklemede',
        icon: <Clock size={18} />,
        color: '#f59e0b',
        bgColor: '#fffbeb',
        borderColor: '#fef3c7'
      },
      processing: {
        label: 'Hazırlanıyor',
        icon: <Package size={18} />,
        color: '#3b82f6',
        bgColor: '#eff6ff',
        borderColor: '#dbeafe'
      },
      shipped: {
        label: 'Kargoda',
        icon: <Truck size={18} />,
        color: '#8b5cf6',
        bgColor: '#f5f3ff',
        borderColor: '#ede9fe'
      },
      delivered: {
        label: 'Teslim Edildi',
        icon: <CheckCircle size={18} />,
        color: '#22c55e',
        bgColor: '#f0fdf4',
        borderColor: '#dcfce7'
      },
      cancelled: {
        label: 'İptal Edildi',
        icon: <XCircle size={18} />,
        color: '#ef4444',
        bgColor: '#fef2f2',
        borderColor: '#fee2e2'
      }
    };

    return statusMap[status] || statusMap.pending;
  };

  // ← DEĞİŞTİ: orderSearchQuery kullan
  const filteredOrders = orders.filter(order => {
    const matchesSearch =
      order.orderNumber?.toLowerCase().includes(orderSearchQuery.toLowerCase()) ||
      order.items?.some(item => item.productName?.toLowerCase().includes(orderSearchQuery.toLowerCase()));

    const matchesFilter = filterStatus === 'all' || order.status === filterStatus;

    return matchesSearch && matchesFilter;
  });

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatTime = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleTimeString('tr-TR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="orders-page">
        <Style />

        <Header
          wishlistCount={wishlistCount}
          cartCount={cartCount}
          searchQuery={searchQuery} // ← DÜZELT
          setSearchQuery={setSearchQuery} // ← DÜZELT
          onSearch={handleHeaderSearch}
          onCartClick={openCartSidebar}
          onWishlistClick={() => navigate('/wishlist')}
          onCampaignsClick={() => navigate('/campaigns')}
          availableCategories={availableCategories}
          products={products}
        />

        <div className="container">
          <div className="loading-container">
            <div className="spinner-large"></div>
            <p>Siparişleriniz yükleniyor...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="orders-page">
      <Style />

      <Header
        wishlistCount={wishlistCount}
        cartCount={cartCount}
        searchQuery={searchQuery} // ← DÜZELT
        setSearchQuery={setSearchQuery} // ← DÜZELT
        onSearch={handleHeaderSearch}
        onCartClick={openCartSidebar}
        onWishlistClick={() => navigate('/wishlist')}
        onCampaignsClick={() => navigate('/campaigns')}
        availableCategories={availableCategories}
        products={products}
      />

      <div className="container">
        {/* Header */}
        <div className="page-header-orders">
          <div className="order-header-content-orders">
            <div className="header-left-orders">
              <h1>
                <Package size={32} />
                Siparişlerim
              </h1>
              <p>Tüm siparişlerinizi buradan takip edebilirsiniz</p>
            </div>

            <div className="header-stats-orders">
              <div className="stat-card">
                <span className="stat-value">{orders.length}</span>
                <span className="stat-label">Toplam Sipariş</span>
              </div>
              <div className="stat-card">
                <span className="stat-value">
                  {orders.filter(o => o.status === 'pending' || o.status === 'processing').length}
                </span>
                <span className="stat-label">Aktif Sipariş</span>
              </div>
              <div className="stat-card">
                <span className="stat-value">
                  {orders.filter(o => o.status === 'delivered').length}
                </span>
                <span className="stat-label">Tamamlanan</span>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="filters-section">
          <div className="search-box">
            <Search size={20} />
            <input
              type="text"
              placeholder="Sipariş numarası veya ürün ara..."
              value={orderSearchQuery} // ← DEĞİŞTİ
              onChange={(e) => setOrderSearchQuery(e.target.value)} // ← DEĞİŞTİ
            />
          </div>

          <div className="filter-buttons">
            <button
              className={`filter-btn ${filterStatus === 'all' ? 'active' : ''}`}
              onClick={() => setFilterStatus('all')}
            >
              Tümü ({orders.length})
            </button>
            <button
              className={`filter-btn ${filterStatus === 'pending' ? 'active' : ''}`}
              onClick={() => setFilterStatus('pending')}
            >
              <Clock size={16} />
              Beklemede ({orders.filter(o => o.status === 'pending').length})
            </button>
            <button
              className={`filter-btn ${filterStatus === 'processing' ? 'active' : ''}`}
              onClick={() => setFilterStatus('processing')}
            >
              <Package size={16} />
              Hazırlanıyor ({orders.filter(o => o.status === 'processing').length})
            </button>
            <button
              className={`filter-btn ${filterStatus === 'shipped' ? 'active' : ''}`}
              onClick={() => setFilterStatus('shipped')}
            >
              <Truck size={16} />
              Kargoda ({orders.filter(o => o.status === 'shipped').length})
            </button>
            <button
              className={`filter-btn ${filterStatus === 'delivered' ? 'active' : ''}`}
              onClick={() => setFilterStatus('delivered')}
            >
              <CheckCircle size={16} />
              Teslim Edildi ({orders.filter(o => o.status === 'delivered').length})
            </button>
          </div>
        </div>

        {/* Orders List */}
        {filteredOrders.length === 0 ? (
          <div className="empty-state">
            <Package size={64} />
            <h3>
              {orderSearchQuery || filterStatus !== 'all'
                ? 'Sipariş Bulunamadı'
                : 'Henüz Siparişiniz Yok'}
            </h3>
            <p>
              {orderSearchQuery || filterStatus !== 'all'
                ? 'Arama kriterlerinize uygun sipariş bulunamadı'
                : 'Alışverişe başlamak için ürünlerimize göz atın'}
            </p>
            <button className="primary-btn" onClick={() => navigate('/')}>
              Alışverişe Başla
            </button>
          </div>
        ) : (
          <div className="orders-grid">
            {filteredOrders.map(order => {
              const statusInfo = getStatusInfo(order.status);
              const itemCount = order.items?.length || 0;

              return (
                <div key={order.id} className="order-card">
                  {/* Order Header */}
                  <div className="order-header-orders">
                    <div className="order-number">
                      <Package size={18} />
                      <span>{order.orderNumber}</span>
                    </div>

                    <div
                      className="order-status"
                      style={{
                        color: statusInfo.color,
                        backgroundColor: statusInfo.bgColor,
                        borderColor: statusInfo.borderColor
                      }}
                    >
                      {statusInfo.icon}
                      <span>{statusInfo.label}</span>
                    </div>
                  </div>

                  {/* Order Date */}
                  <div className="order-date">
                    <Calendar size={14} />
                    <span>{formatDate(order.createdAt)}</span>
                    <span className="time">{formatTime(order.createdAt)}</span>
                  </div>

                  {/* Order Items Preview */}
                  <div className="order-items">
                    {order.items?.slice(0, 3).map((item, index) => (
                      <div key={index} className="order-item">
                        <img
                          src={item.productImage || '/placeholder.png'}
                          alt={item.productName}
                        />
                        <div className="item-info">
                          <p className="item-name">{item.productName}</p>
                          <span className="item-qty">{item.quantity} adet</span>
                        </div>
                        <span className="item-price">{(item.price * item.quantity).toFixed(2)}₺</span>
                      </div>
                    ))}

                    {itemCount > 3 && (
                      <p className="more-items">+{itemCount - 3} ürün daha</p>
                    )}
                  </div>

                  {/* Order Footer */}
                  <div className="order-footer">
                    <div className="order-info">
                      <div className="info-item">
                        <MapPin size={14} />
                        <span>{order.shippingAddress?.city}</span>
                      </div>
                      <div className="info-item">
                        <CreditCard size={14} />
                        <span>
                          {order.paymentMethod === 'credit-card'
                            ? 'Kredi Kartı'
                            : 'Kapıda Ödeme'}
                        </span>
                      </div>
                    </div>

                    <div className="order-total">
                      <span>Toplam:</span>
                      <strong>{order.total?.toFixed(2)}₺</strong>
                    </div>
                  </div>

                  {/* Order Actions */}
                  <div className="order-actions">
                    <button
                      className="view-detail-btn"
                      onClick={() => navigate(`/orders/${order.id}`)}
                    >
                      <Eye size={16} />
                      Sipariş Detayı
                      <ChevronRight size={16} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}

function Style() {
  return (
    <style>{`
      * {
        margin: 0;
        padding: 0;
        box-sizing: border-box;
      }

      body {
        font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
        background: #f8f9fa;
      }

      .orders-page {
        min-height: 100vh;
        padding: 0 0 0px;
      }

      .container {
        max-width: 1320px;
        margin: 0 auto;
        padding: 0 20px;
      }

      /* =============== PAGE CONTENT =============== */

      /* Loading */
      .loading-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        min-height: 60vh;
        gap: 20px;
      }

      .spinner-large {
        width: 60px;
        height: 60px;
        border: 4px solid #e9ecef;
        border-top-color: #E5332A;
        border-radius: 50%;
        animation: spin 1s linear infinite;
      }

      @keyframes spin {
        to { transform: rotate(360deg); }
      }

      .page-header-orders {
        margin-bottom: 32px;
      }

      .order-header-content-orders {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        gap: 32px;
        background: linear-gradient(135deg, #E5332A 0%, #c72a22 100%);
        padding: 40px;
        border-radius: 24px;
        color: #fff;
        margin-top: 20px;
      }

      .header-left-orders h1 {
        display: flex;
        align-items: center;
        gap: 16px;
        font-size: 32px;
        margin-bottom: 8px;
      }

      .header-left-orders p {
        font-size: 16px;
        opacity: 0.9;
      }

      .header-stats-orders {
        display: flex;
        gap: 24px;
      }

      .stat-card {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 8px;
        padding: 20px 32px;
        background: rgba(255, 255, 255, 0.15);
        backdrop-filter: blur(10px);
        border-radius: 16px;
        border: 1px solid rgba(255, 255, 255, 0.2);
      }

      .stat-value {
        font-size: 32px;
        font-weight: 700;
      }

      .stat-label {
        font-size: 13px;
        opacity: 0.9;
      }

      /* Filters */
      .filters-section {
        display: flex;
        gap: 20px;
        margin-bottom: 32px;
        flex-wrap: wrap;
      }

      .search-box {
        flex: 1;
        min-width: 300px;
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 14px 20px;
        background: #fff;
        border: 2px solid #e9ecef;
        border-radius: 12px;
        transition: all 0.2s;
      }

      .search-box:focus-within {
        border-color: #E5332A;
        box-shadow: 0 0 0 4px rgba(229, 51, 42, 0.1);
      }

      .search-box input {
        flex: 1;
        border: none;
        outline: none;
        font-size: 14px;
      }

      .filter-buttons {
        display: flex;
        gap: 12px;
        flex-wrap: wrap;
      }

      .filter-btn {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 12px 20px;
        background: #fff;
        border: 2px solid #e9ecef;
        border-radius: 12px;
        font-weight: 600;
        font-size: 14px;
        color: #495057;
        cursor: pointer;
        transition: all 0.2s;
        white-space: nowrap;
      }

      .filter-btn:hover {
        border-color: #E5332A;
        color: #E5332A;
      }

      .filter-btn.active {
        background: #E5332A;
        border-color: #E5332A;
        color: #fff;
      }

      /* Orders Grid */
      .orders-grid {
        display: grid;
        gap: 24px;
      }

      .order-card {
        background: #fff;
        border-radius: 20px;
        padding: 24px;
        border: 2px solid #e9ecef;
        transition: all 0.3s;
        margin-bottom: 32px;
      }

      .order-card:hover {
        border-color: #E5332A;
        box-shadow: 0 8px 24px rgba(229, 51, 42, 0.1);
        transform: translateY(-2px);
      }

      /* Order Header */
      .order-header-orders {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 16px;
        padding-bottom: 16px;
        border-bottom: 2px solid #f8f9fa;
      }

      .order-number {
        display: flex;
        align-items: center;
        gap: 10px;
        font-weight: 700;
        font-size: 18px;
        color: #2B2B2B;
      }

      .order-status {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 8px 16px;
        border-radius: 10px;
        font-weight: 600;
        font-size: 14px;
        border: 2px solid;
      }

      /* Order Date */
      .order-date {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 14px;
        color: #868e96;
        margin-bottom: 20px;
      }

      .order-date .time {
        padding-left: 8px;
        border-left: 1px solid #e9ecef;
      }

      /* Order Items */
      .order-items {
        margin-bottom: 20px;
      }

      .order-item {
        display: flex;
        gap: 16px;
        padding: 16px;
        background: #f8f9fa;
        border-radius: 12px;
        margin-bottom: 12px;
      }

      .order-item img {
        width: 60px;
        height: 60px;
        object-fit: contain;
        background: #fff;
        border-radius: 8px;
        padding: 4px;
        flex-shrink: 0;
      }

      .item-info {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 4px;
        min-width: 0;
      }

      .item-name {
        font-weight: 600;
        color: #2B2B2B;
        font-size: 14px;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }

      .item-qty {
        font-size: 13px;
        color: #868e96;
      }

      .item-price {
        font-weight: 700;
        color: #E5332A;
        font-size: 16px;
        flex-shrink: 0;
      }

      .more-items {
        text-align: center;
        padding: 12px;
        background: #f8f9fa;
        border-radius: 8px;
        font-size: 13px;
        color: #868e96;
        font-weight: 600;
      }

      /* Order Footer */
      .order-footer {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 16px 0;
        border-top: 2px solid #f8f9fa;
        border-bottom: 2px solid #f8f9fa;
        margin-bottom: 16px;
      }

      .order-info {
        display: flex;
        gap: 24px;
      }

      .info-item {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 13px;
        color: #868e96;
      }

      .order-total {
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        gap: 4px;
      }

      .order-total span {
        font-size: 13px;
        color: #868e96;
      }

      .order-total strong {
        font-size: 24px;
        color: #E5332A;
      }

      /* Order Actions */
      .order-actions {
        display: flex;
        gap: 12px;
      }

      .view-detail-btn {
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        padding: 14px 24px;
        background: #E5332A;
        border: none;
        border-radius: 12px;
        color: #fff;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.2s;
      }

      .view-detail-btn:hover {
        background: #c72a22;
        transform: translateX(4px);
      }

      /* Empty State */
      .empty-state {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 80px 20px;
        text-align: center;
      }

      .empty-state svg {
        color: #e9ecef;
        margin-bottom: 24px;
      }

      .empty-state h3 {
        font-size: 24px;
        margin-bottom: 12px;
        color: #2B2B2B;
      }

      .empty-state p {
        font-size: 16px;
        color: #868e96;
        margin-bottom: 32px;
        max-width: 500px;
      }

      .primary-btn {
        padding: 14px 32px;
        background: #E5332A;
        color: #fff;
        border: none;
        border-radius: 12px;
        font-weight: 600;
        font-size: 16px;
        cursor: pointer;
        transition: all 0.2s;
      }

      .primary-btn:hover {
        background: #c72a22;
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(229, 51, 42, 0.3);
      }

      /* ============ RESPONSIVE ============ */
      @media (max-width: 992px) {
        .order-header-content-orders {
          flex-direction: column;
          padding: 32px;
        }

        .header-stats-orders {
          width: 100%;
          justify-content: space-between;
        }

        .stat-card {
          flex: 1;
          padding: 16px 24px;
        }

        .stat-value {
          font-size: 28px;
        }

        .contact-info {
          display: none;
        }

        .order-card {
          padding: 20px;
        }

        .order-footer {
          flex-wrap: wrap;
          gap: 16px;
        }
      }

      @media (max-width: 768px) {
        .container {
          padding: 0 16px;
        }

        .orders-page {
          padding: 0 0 60px;
        }

        .page-header-orders {
          margin-bottom: 24px;
        }

        .order-header-content-orders {
          padding: 24px 20px;
          border-radius: 20px;
          margin-top: 16px;
        }

        .header-left-orders h1 {
          font-size: 22px;
          gap: 12px;
        }

        .header-left-orders h1 svg {
          width: 24px;
          height: 24px;
        }

        .header-left-orders p {
          font-size: 14px;
        }

        .header-stats-orders {
          flex-direction: column;
          gap: 12px;
        }

        .stat-card {
          flex-direction: row;
          justify-content: space-between;
          padding: 16px 20px;
        }

        .stat-value {
          font-size: 24px;
        }

        .stat-label {
          font-size: 12px;
        }

        /* ✅ FILTER BUTTONS SCROLL */
        .filters-section {
          flex-direction: column;
          gap: 16px;
          margin-bottom: 24px;
          isolation: isolate;
        }

        .search-box {
          min-width: 100%;
          padding: 12px 16px;
        }

        .search-box input {
          font-size: 13px;
        }

        .filter-buttons {
          display: flex;
          gap: 8px;
          padding-bottom: 8px;
          overflow-x: scroll !important;
          overflow-y: hidden !important;
          flex-wrap: nowrap;
          touch-action: pan-x !important;
          overscroll-behavior-x: contain !important;
          overscroll-behavior-y: none !important;
          scrollbar-width: none;
          -ms-overflow-style: none;
          -webkit-overflow-scrolling: touch;
          position: relative;
          z-index: 1;
          -webkit-user-select: none;
          user-select: none;
        }

        .filter-buttons::-webkit-scrollbar {
          display: none;
        }

        .filter-btn {
          white-space: nowrap;
          padding: 10px 16px;
          font-size: 13px;
          flex-shrink: 0;
          touch-action: manipulation;
          -webkit-tap-highlight-color: transparent;
          user-select: none;
          pointer-events: auto;
        }

        /* Order Cards */
        .orders-grid {
          gap: 16px;
        }

        .order-card {
          padding: 16px;
          border-radius: 16px;
          margin-bottom: 16px;
        }

        .order-card:hover {
          transform: none;
        }

        .order-header-orders {
          flex-wrap: wrap;
          gap: 12px;
          margin-bottom: 12px;
          padding-bottom: 12px;
        }

        .order-number {
          font-size: 16px;
          gap: 8px;
        }

        .order-number svg {
          width: 18px;
          height: 18px;
        }

        .order-status {
          padding: 6px 12px;
          font-size: 12px;
        }

        .order-date {
          font-size: 13px;
          flex-wrap: wrap;
          margin-bottom: 16px;
        }

        .order-date .time {
          padding-left: 0;
          border-left: none;
        }

        .order-items {
          margin-bottom: 16px;
        }

        .order-item {
          padding: 12px;
          gap: 12px;
        }

        .order-item img {
          width: 50px;
          height: 50px;
        }

        .item-name {
          font-size: 13px;
          -webkit-line-clamp: 1;
        }

        .item-qty {
          font-size: 12px;
        }

        .item-price {
          font-size: 14px;
        }

        .more-items {
          padding: 10px;
          font-size: 12px;
        }

        .order-footer {
          flex-direction: column;
          gap: 16px;
          align-items: stretch;
          padding: 12px 0;
        }

        .order-info {
          flex-direction: column;
          gap: 8px;
        }

        .info-item {
          font-size: 12px;
        }

        .info-item svg {
          width: 14px;
          height: 14px;
        }

        .order-total {
          align-items: flex-start;
          flex-direction: row;
          justify-content: space-between;
          align-items: center;
        }

        .order-total span {
          font-size: 12px;
        }

        .order-total strong {
          font-size: 20px;
        }

        .order-actions {
          gap: 8px;
        }

        .view-detail-btn {
          padding: 12px 20px;
          font-size: 14px;
        }

        .view-detail-btn:hover {
          transform: none;
        }

        .empty-state {
          padding: 60px 20px;
        }

        .empty-state svg {
          width: 80px;
          height: 80px;
        }

        .empty-state h3 {
          font-size: 20px;
        }

        .empty-state p {
          font-size: 14px;
          margin-bottom: 24px;
        }

        .primary-btn {
          padding: 12px 24px;
          font-size: 14px;
          width: 100%;
        }

        .header-content-main-orders {
          gap: 16px;
        }

        .header-actions-orders {
          width: 100%;
          justify-content: space-between;
        }

        .top-links {
          display: none;
        }
      }

      /* ============ KÜÇÜK MOBİL (480px ve altı) - DETAYLI ============ */
      @media (max-width: 480px) {
        /* Container */
        .container {
          padding: 0 12px;
        }

        /* Page */
        .orders-page {
          padding: 0 0 60px;
        }

        /* Loading */
        .spinner-large {
          width: 48px;
          height: 48px;
          border: 3px solid #e9ecef;
          border-top-color: #E5332A;
        }

        .loading-container p {
          font-size: 14px;
        }

        /* Header */
        .page-header-orders {
          margin-bottom: 20px;
        }

        .order-header-content-orders {
          padding: 18px 14px;
          border-radius: 16px;
          margin-top: 12px;
          gap: 20px;
        }

        .header-left-orders h1 {
          font-size: 18px;
          gap: 10px;
        }

        .header-left-orders h1 svg {
          width: 20px;
          height: 20px;
        }

        .header-left-orders p {
          font-size: 13px;
        }

        /* Stats */
        .header-stats-orders {
          gap: 10px;
        }

        .stat-card {
          padding: 12px 14px;
          border-radius: 12px;
        }

        .stat-value {
          font-size: 20px;
        }

        .stat-label {
          font-size: 11px;
        }

        /* Filters */
        .filters-section {
          gap: 12px;
          margin-bottom: 20px;
        }

        .search-box {
          padding: 10px 14px;
          border-radius: 10px;
          width: 100%;
        }

        .search-box svg {
          width: 16px;
          height: 16px;
        }

        .search-box input {
          font-size: 12px;
        }

        .filter-buttons {
          gap: 6px;
          padding-bottom: 6px;
          width: 100%;
        }

        .filter-btn {
          padding: 12px 12px;
          font-size: 11px;
          border-radius: 8px;
          gap: 4px;
        }

        .filter-btn svg {
          width: 14px;
          height: 14px;
        }

        /* Orders Grid */
        .orders-grid {
          gap: 12px;
        }

        /* Order Card */
        .order-card {
          padding: 12px;
          border-radius: 14px;
          margin-bottom: 12px;
        }

        /* Order Header */
        .order-header-orders {
          gap: 10px;
          margin-bottom: 10px;
          padding-bottom: 10px;
        }

        .order-number {
          font-size: 14px;
          gap: 6px;
        }

        .order-number svg {
          width: 16px;
          height: 16px;
        }

        .order-status {
          padding: 5px 10px;
          font-size: 11px;
          border-radius: 8px;
          gap: 4px;
        }

        .order-status svg {
          width: 12px;
          height: 12px;
        }

        /* Order Date */
        .order-date {
          font-size: 12px;
          gap: 6px;
          margin-bottom: 12px;
        }

        .order-date svg {
          width: 12px;
          height: 12px;
        }

        /* Order Items */
        .order-items {
          margin-bottom: 12px;
        }

        .order-item {
          padding: 10px;
          gap: 10px;
          border-radius: 10px;
          margin-bottom: 8px;
        }

        .order-item img {
          width: 42px;
          height: 42px;
          border-radius: 6px;
        }

        .item-name {
          font-size: 12px;
          -webkit-line-clamp: 1;
        }

        .item-qty {
          font-size: 11px;
        }

        .item-price {
          font-size: 13px;
        }

        .more-items {
          padding: 8px;
          font-size: 11px;
          border-radius: 6px;
        }

        /* Order Footer */
        .order-footer {
          gap: 12px;
          padding: 10px 0;
        }

        .order-info {
          gap: 6px;
        }

        .info-item {
          font-size: 11px;
          gap: 4px;
        }

        .info-item svg {
          width: 12px;
          height: 12px;
        }

        .order-total span {
          font-size: 11px;
        }

        .order-total strong {
          font-size: 18px;
        }

        /* Order Actions */
        .order-actions {
          gap: 6px;
        }

        .view-detail-btn {
          padding: 10px 16px;
          font-size: 13px;
          border-radius: 10px;
          gap: 6px;
        }

        .view-detail-btn svg {
          width: 14px;
          height: 14px;
        }

        /* Empty State */
        .empty-state {
          padding: 40px 16px;
        }

        .empty-state svg {
          width: 64px;
          height: 64px;
          margin-bottom: 16px;
        }

        .empty-state h3 {
          font-size: 18px;
          margin-bottom: 10px;
        }

        .empty-state p {
          font-size: 13px;
          margin-bottom: 20px;
          line-height: 1.5;
        }

        .primary-btn {
          padding: 10px 20px;
          font-size: 13px;
          border-radius: 10px;
        }
      }

      /* ============ ÇOK KÜÇÜK MOBİL (360px ve altı) ============ */
      @media (max-width: 360px) {
        .container {
          padding: 0 10px;
        }

        .order-header-content-orders {
          padding: 16px 12px;
        }

        .header-left-orders h1 {
          font-size: 16px;
        }

        .stat-card {
          padding: 10px 12px;
        }

        .stat-value {
          font-size: 18px;
        }

        .order-card {
          padding: 10px;
        }

        .order-item img {
          width: 38px;
          height: 38px;
        }

        .item-name {
          font-size: 11px;
        }

        .view-detail-btn {
          padding: 8px 14px;
          font-size: 12px;
        }
      }
    `}</style>
  );
}